/* eslint-disable max-statements */

import { LoadingOverlay, Stack } from '@mantine/core';
import { UseFormReturnType } from '@mantine/form';
import { useEffect, useState } from 'react';

import { DocumentsEnum } from '@e-origin/shared';

import { AsyncAutocomplete, DatePickerInput, MultiLangCodes, TextInput } from '@components';
import { useDeclarant } from '@hooks/declarant';
import { useCodeListSystem } from '@hooks/useCodeListSystem';

export enum FormDocumentsInputType {
  NUMBER = 'NUMBER',
  INPUT = 'INPUT',
  AUTOCOMPLETE = 'AUTOCOMPLETE',
  MULTI_LANG = 'MULTI_LANG',
  DATE = 'DATE',
}

export type DocumentFormSchemaProps = Record<
  string,
  {
    fields: Record<
      string,
      {
        type: FormDocumentsInputType;
        search?: (search: string, lang: string, isHeader?: boolean) => Promise<{ value: string; label: string }[]>;
        placeholder: string;
        code?: string;
        hide?: boolean;
        disabled?: boolean;
        'data-pw'?: string;
      }
    >;
  }
>;
interface DocumentFormFactoryProps {
  type: DocumentsEnum;
  editDisabled: boolean;
  form: UseFormReturnType<any>;
  documentFormSchema: DocumentFormSchemaProps;
  pathPrefix?: string;
}

export const DocumentFormFactory: React.FC<DocumentFormFactoryProps> = ({ type, editDisabled, form, documentFormSchema, pathPrefix }) => {
  const { data: declarant, isLoading } = useDeclarant();
  const [formSchema, setFormSchema] = useState({} as any);
  const codelistSystems = useCodeListSystem({});

  useEffect(() => {
    setFormSchema(documentFormSchema[type]);
  }, [type]);

  if (!declarant) {
    return <LoadingOverlay visible={isLoading} overlayProps={{ radius: 'sm', blur: 2 }} />;
  }

  return (
    <Stack>
      {formSchema?.fields &&
        Object.keys(formSchema.fields).map((path, idx) => {
          const { type: fieldType, placeholder, hide, code, 'data-pw': dataPw, disabled } = formSchema.fields[path];

          if (hide) {
            return null;
          }
          const fieldPath = pathPrefix ? `${pathPrefix}.${path}` : path;

          if (fieldType === FormDocumentsInputType.AUTOCOMPLETE) {
            return (
              <AsyncAutocomplete
                {...form.getInputProps(fieldPath)}
                key={`${type}-${idx}`}
                fetchOptions={(search) => formSchema.fields[path].search(search, declarant.language)}
                placeholder={placeholder}
                disabled={editDisabled || disabled}
              />
            );
          }

          if (fieldType === FormDocumentsInputType.MULTI_LANG) {
            return (
              <MultiLangCodes
                {...form.getInputProps(fieldPath)}
                key={`${type}-${idx}-${code}`}
                label={placeholder}
                code={code}
                system={codelistSystems}
                disabled={editDisabled || disabled}
                data-pw={dataPw}
              />
            );
          }

          if (fieldType === FormDocumentsInputType.INPUT) {
            return (
              <TextInput key={`${type}-${idx}`} {...form.getInputProps(fieldPath)} label={placeholder} disabled={editDisabled || disabled} data-pw={dataPw} />
            );
          }

          if (fieldType === FormDocumentsInputType.DATE) {
            return <DatePickerInput key={`${type}-${idx}`} {...form.getInputProps(fieldPath)} label={placeholder} disabled={editDisabled || disabled} />;
          }

          return null;
        })}
    </Stack>
  );
};
